import { RSAA } from 'redux-api-middleware';
import { Dispatch } from 'redux';
import { TGetRootState } from 'types/shared';
import * as Sentry from '@sentry/react';
import customFailure from 'helpers/customFailureAction';
import { TTableHeader } from 'components/CustomizeView/CustomizeView';
import getRuntimeEnv from "helpers/env";

const SAVE_REQUEST = 'opus/custom-list/SAVE_REQUEST';
const SAVE_SUCCESS = 'opus/custom-list/SAVE_SUCCESS';
const SAVE_FAILURE = 'opus/custom-list/SAVE_FAILURE';
const LOAD_REQUEST = 'opus/custom-list/REQUEST';
const LOAD_SUCCESS = 'opus/custom-list/SUCCESS';
const LOAD_FAILURE = 'opus/custom-list/FAILURE';

type ActionTypes =
  | typeof SAVE_REQUEST
  | typeof SAVE_SUCCESS
  | typeof SAVE_FAILURE
  | typeof LOAD_REQUEST
  | typeof LOAD_SUCCESS
  | typeof LOAD_FAILURE;

interface IAction {
  type: ActionTypes;
  payload: { data: TTableHeader[]; tableType: string };
}

interface IHeadersPartialState {
  pending: boolean;
  fulfilled: boolean;
  rejected: boolean;
  reason: any;
  data: TTableHeader[];
}

interface IHeadersState {
  home: IHeadersPartialState;
  customList: IHeadersPartialState;
  playlist: IHeadersPartialState;
  streamingTerritories: IHeadersPartialState;
}

const initialState: IHeadersState = {
  home: {
    pending: false,
    fulfilled: false,
    rejected: false,
    reason: null,
    data: [],
  },
  customList: {
    pending: false,
    fulfilled: false,
    rejected: false,
    reason: null,
    data: [],
  },
  playlist: {
    pending: false,
    fulfilled: false,
    rejected: false,
    reason: null,
    data: [],
  },
  streamingTerritories: {
    pending: false,
    fulfilled: false,
    rejected: false,
    reason: null,
    data: [],
  },
};

export default function reducer(
  state: IHeadersState = initialState,
  action: IAction
): IHeadersState {
  const { type, payload } = action;
  switch (type) {
    case LOAD_REQUEST: {
      return {
        ...state,
        [payload.tableType]: {
          pending: true,
          fulfilled: false,
          rejected: false,
        },
      };
    }
    case LOAD_SUCCESS:
    case SAVE_SUCCESS: {
      return {
        ...state,
        [action.payload.tableType]: {
          pending: false,
          fulfilled: true,
          rejected: false,
          data: payload.data ?? [],
        },
      };
    }
    case LOAD_FAILURE: {
      return {
        ...state,
        [payload.tableType]: {
          pending: false,
          fulfilled: false,
          rejected: true,
          reason: payload,
          data: (state as any)[payload.tableType].data,
        },
      };
    }
    default:
      return state;
  }
}

const generateURL = (
  getRootState: TGetRootState,
  tableType: string
): string => {
  const { id } = getRootState().authentication.profile;
  /** 'customList' for custom list BrowseTable
   * 'home' for BrowseOpus at Home */
  if (tableType === 'customList') return `/kv/user_${id}_custom_list`;
  return `/kv/user_${id}_browseTable_${tableType}`;
};

/**
 * This action creator initializes an empty object at endpoint for a user.
 * Only called once per user, when we get a 404 while trying to load the headers.
 */
function initUsageEndpoint(
  tableType: string
): (dispatch: Dispatch, getState: TGetRootState) => Promise<any> {
  return async (dispatch: Dispatch, getState: TGetRootState): Promise<any> => {
    await fetch(
      new Request(generateURL(getState, tableType), {
        body: JSON.stringify({
          data: [],
        }),
        method: 'PUT',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
      })
    );
  };
}

export const loadRecentHeaders = (
  tableType: string
): ((dispatch: Dispatch, getState: TGetRootState) => Promise<any>) => {
  return async function (
    dispatch: Dispatch,
    getState: TGetRootState
  ): Promise<any> {
    // This returns the settings from the API
    try {
      const actionResponse = await dispatch({
        [RSAA]: {
          types: [
            {
              type: LOAD_REQUEST,
              payload: { tableType },
            },
            {
              type: LOAD_SUCCESS,
              payload: async function payload(
                action: IAction,
                state: IHeadersState,
                res: any
              ): Promise<any> {
                const json = await res.json();
                return { data: json?.data, tableType };
              },
            },
            {
              type: LOAD_FAILURE,
              payload: async (
                action: IAction,
                state: IHeadersState,
                res: any
              ): Promise<any> => {
                const error = await customFailure(res);
                return {
                  ...error,
                  tableType,
                };
              },
            },
          ],
          endpoint: generateURL(getState, tableType),
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
        },
      });

      // This should only happen once for every user
      if ((actionResponse as any).error) {
        if ((actionResponse as any).payload.status === 404) {
          return await dispatch(initUsageEndpoint(tableType) as any);
        }
      }

      return actionResponse;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('There was an error loading your recent filters');
      // eslint-disable-next-line no-console
      console.error(error);
    }
  };
};

/**
 * This action sends updated headers of the table of the browse table headers to the KV API and save the current state into redux store
 */
export function saveToApi(
  data: TTableHeader[],
  tableType: string
): (dispatch: Dispatch, getState: TGetRootState) => Promise<any> {
  return async function (
    dispatch: Dispatch,
    getState: TGetRootState
  ): Promise<any> {
    try {
      const response = await dispatch({
        [RSAA]: {
          types: [
            SAVE_REQUEST,
            {
              type: SAVE_SUCCESS,
              payload: { data, tableType },
            },
            SAVE_FAILURE,
          ],
          endpoint: generateURL(getState, tableType),
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ data }),
        },
      });

      if (response.error) {
        throw response.payload;
      }

      return response;
    } catch (error) {
      // The final retry error will end up here. Log it to console.

      // eslint-disable-next-line no-console
      console.warn(
        'Your headers for the current custom list failed to be saved remotely.'
      );
      // eslint-disable-next-line no-console
      console.error(error);

      // Report it to Sentry
      if (getRuntimeEnv().NODE_ENV === 'production') {
        // Send the exception to Sentry
        Sentry.captureException(error);
      }
    }
  };
}
